import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { ReactComponent as SendIcon } from "../icons/SendIcon.svg";
import { ReactComponent as Avatar } from "../icons/ChatbotIcon.svg";
import { ReactComponent as ChatIcon } from "../icons/ChatIcon.svg";
import { MessageSchema, MembershipSchema } from "../data/types";
import { useMemberState } from "../contexts/membercontext";
import { useSendMessage } from "../hooks/memberhooks";
import UserAvatar from '../components/UserAvatar';

// const capitalizeString = (string: string) =>
//   string?.replace(/\w/, (c) => c?.toUpperCase());

type ChatWindowProps = {
  currentMembership: MembershipSchema
}

const ChatWindow: React.FC<ChatWindowProps> = ({ currentMembership }) => {
  const [currentText, updateCurrentText] = useState("");
  const [imageError, setImageError] = useState(false);
  var { communityId, currentChatMember, messageThread, updateMessageThread, setMessageOpen } =
    useMemberState();
  const { sendMessage } = useSendMessage();
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!currentChatMember || !messageThread?.length) return;

    const scrollToBottom = () => {
      bottomRef.current?.scrollIntoView?.({ behavior: "auto", block: "end" });
    };

    scrollToBottom();
    const frame = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frame);
  }, [messageThread, currentChatMember]);

  const formatMessageContent = (content: string) => {
    // Split the content by ** and map through segments
    const segments = content?.split(/(\*\*.*?\*\*)/g);
    return segments?.map((segment: string, index: number) => {
      if (segment.startsWith('**') && segment.endsWith('**')) {
        // Remove ** and wrap in bold span
        const boldText = segment.slice(2, -2);
        return <span key={index} className="font-bold">{boldText}</span>;
      }
      return segment;
    });
  };

  const handleSubmit = async () => {
    if (currentChatMember && currentText.trim()) {
      let messages: MessageSchema[] = [
        ...messageThread,
        {
          content: currentText,
          type: "sent",
          read_status: false,
          // username: currentChatMember,
          uid: currentChatMember,
          community_id: communityId,
          created: new Date().toISOString(),
        },
      ];
      updateMessageThread(messages);
      sendMessage(currentChatMember, currentText);
      updateCurrentText("");
    }
  };

  // Dummy date for divider (replace with real logic as needed)
  const dateString = moment().format('ddd MMM DD, YYYY');

  if (!currentChatMember) {
    return (
      <div>
        <div className="flex flex-col justify-start align-middle w-full bg-[#fff] p-0 rounded-md font-open-sans h-[90vh]">
          <div className=" w-[100%] ">
            <div className="flex justify-center aling-middle m-auto">
              <div className="flex flex-col gap-2 justify-center text-[#013847] font-thin text-[14px] pt-24 text-center">
                {/* Placeholder for empty state */}
                <div className="flex flex-col gap-2 justify-center text-[#013847] font-thin text-[14px] pt-24 text-center">
                  <div className="flex mx-auto justify-center items-center bg-[#EFF8FA]/90 h-20 w-20 rounded-full p-2">
                    <ChatIcon className="w-10 h-10 text-[#5DB6CE]" />
                  </div>
                </div>

                <p className="text-[20px] font-raleway font-semibold text-[#013847]">
                  Communicate with Your Members
                </p>
                <p className=" text-[#333333] text-center place-self-center font-open-sans text-[16px]">
                  Your conversations will show up here.
                </p>
                <button onClick={() => setMessageOpen(true)} className="flex items-center justify-center bg-primary-200 w-full font-raleway font-normal text-white place-self-center p-2 mt-4 rounded-lg text-[18px]">
                  <ChatIcon className="w-[24px] h-[24px] mr-2" />
                  Send a Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col justify-between align-top w-full bg-[#fff] p-0 rounded-md font-open-sans h-[90vh] relative">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-2 border-[#E9E9E9]/90 border-b-[1px]">
          {currentChatMember === "chatbot" ? (
            <Avatar className="h-10 w-10 text-[#51a0b5]/90" />
          ) : (
            currentMembership?.avatar && !imageError ? (
              <img
                src={currentMembership.avatar}
                alt="User Avatar"
                className="h-10 w-10 rounded-full object-fit"
                onError={() => setImageError(true)}
              />
            ) : (
              <UserAvatar
                firstName={currentMembership?.first_name}
                lastName={currentMembership?.last_name}
                username={currentMembership?.username}
                email={currentMembership?.email}
                size="md"
              />
            )
          )}
          <div className="flex flex-col">
            <span className="text-[16px] text-[#2D3748] font-bold leading-tight">{currentChatMember === "chatbot" ? 'HomeCook AI' : (!currentMembership?.first_name && !currentMembership?.last_name)
                            ? (currentMembership?.username || currentMembership?.email || '')
                            : [currentMembership?.first_name, currentMembership?.last_name].filter(Boolean).join(' ')}
            </span>
            {currentChatMember !== "chatbot" && (<span className="text-[13px] text-[#A0AEC0] font-normal">Online</span>)}
          </div>
        </div>
        {/* Date Divider */}
        <div className="flex justify-center items-center my-2">
          <span className="bg-transparent text-[#A0AEC0] text-xs px-4 py-1 rounded-full font-medium">Today, {dateString}</span>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 pb-4" style={{ minHeight: '0' }}>
          {messageThread && messageThread?.length > 0 && messageThread?.map((m: MessageSchema, i: number) => {
            const isReceived = m?.type === "received";
            const isImage = m?.content && m.content.startsWith('http'); // crude image check
            return (
              <div key={i} className={`flex ${isReceived ? 'justify-start' : 'justify-end'} mb-4 w-full`}>
                {isReceived && (
                  currentChatMember === "chatbot" ? (
                    <Avatar className="h-8 w-8 mr-2 mt-auto text-[#51a0b5]/90" />
                  ) : (
                    currentMembership?.avatar && !imageError ? (
                      <img
                        src={currentMembership.avatar}
                        alt="User Avatar"
                        className="h-8 w-8 rounded-full object-fit object-center mr-2 mt-auto"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <UserAvatar
                        firstName={currentMembership?.first_name}
                        lastName={currentMembership?.last_name}
                        username={currentMembership?.username}
                        email={currentMembership?.email}
                        size="sm"
                        className="mr-2 mt-auto"
                      />
                    )
                  )
                )}
                <div className={`flex flex-col max-w-[70%] ${isReceived ? '' : 'items-end'}`}>
                  <div className={`rounded-2xl ${isReceived ? 'bg-white border border-[#E2E8F0]' : 'bg-[#F7F7F7]'} px-4 py-3 shadow-sm`}>
                    {isImage ? (
                      <img src={m.content} alt="attachment" className="rounded-xl max-w-full max-h-48 object-cover" />
                    ) : (
                      <span className="text-[15px] text-[#2D3748] whitespace-pre-line">
                        {formatMessageContent(m.content)}
                      </span>
                    )}
                  </div>
                  <span className="text-[12px] text-[#A0AEC0] mt-1 mr-1 self-end">2:14 PM</span>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} aria-hidden="true" />
        </div>
        {/* Input Bar */}
        <div className="w-full px-6 py-4 bg-white border-t border-[#F1F1F1] flex items-center gap-2 sticky bottom-0 z-10">
          <button className="flex items-center font-light justify-center w-6 h-6 rounded-full bg-white border-[1px] border-[#8A8A8A] text-[#8A8A8A] text-xl  mr-2">+</button>
          <input
            className="flex-1 placeholder:text-[#808080]/50 placeholder:font-raleway text-black rounded-full bg-[#F7F7F7] px-5 py-3 text-[15px] outline-none border-none shadow-sm"
            placeholder="Type a message"
            value={currentText}
            onChange={e => updateCurrentText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
          />
          <button onClick={handleSubmit} className="ml-2 flex items-center justify-center w-9 h-9 rounded-full hover:bg-[#4299a7]">
            <SendIcon className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
      </div>
    );
  }
};

export default ChatWindow;
